/**
 * Bulk Order JavaScript
 * Replicates the current product page vehicle selection logic for multiple products
 * Based on the existing buy-buttons.liquid vehicle selection implementation
 */

class BulkOrderManager {
  constructor() {
    this.vehicles = [];
    this.config = {};
    this.isInitialized = false;
    
    // Global filter elements
    this.filterYearSelect = null;
    this.filterModelSelect = null;
    this.filterProductRows = [];
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  async init() {
    try {
      console.log('🚚 Initializing Bulk Order Manager...');
      
      // Load configuration
      this.loadConfig();
      
      // Cache DOM elements
      this.cacheElements();
      
      // Set up event listeners
      this.setupEventListeners();
      
      // Load vehicle data
      await this.loadVehicles();
      
      // Initialize global vehicle filters
      this.initializeGlobalFilters();

      // Show overlay initially (no vehicle selected)
      this.updateOverlayVisibility();
      
      // Individual vehicle selectors removed - now using global filters
      
      // Initialize product rows
      this.initializeProductRows();
      
      this.isInitialized = true;
      console.log('✅ Bulk Order Manager initialized successfully');
      
    } catch (error) {
      console.error('❌ Error initializing Bulk Order Manager:', error);
    }
  }

  loadConfig() {
    const configEl = document.getElementById('bulk-order-config');
    if (configEl) {
      this.config = JSON.parse(configEl.textContent);
    }
    console.log('📋 Loaded configuration:', this.config);
  }

  cacheElements() {
    console.log("[cacheElements] Caching elements...");
    // Form and controls
    this.bulkForm = document.getElementById('bulk-order-form');
    this.selectAllBtn = document.getElementById('select-all-btn');
    this.clearAllBtn = document.getElementById('clear-all-btn');
    this.submitBtn = document.getElementById('bulk-order-submit-btn');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    
    // Global filter elements
    this.filterYearSelect = document.getElementById('filter-year');
    this.filterModelSelect = document.getElementById('filter-model');
    this.clearFilterBtn = document.getElementById('clear-vehicle-filter');
    this.filterProductRows = [...document.querySelectorAll('.product-row')];
    
    // Summary elements
    this.summaryCount = document.querySelector('.bulk-order-summary__count');
    this.summaryPrice = document.querySelector('.bulk-order-summary__price');
    
    // Product elements
    this.productRows = document.querySelectorAll('.bulk-order-row');
    this.quantityInputs = document.querySelectorAll('.quantity__input'); // Standard class for Shopify quantity inputs
    console.log('[cacheElements] Found quantity inputs:', this.quantityInputs.length, this.quantityInputs);
    this.chargerSelects = document.querySelectorAll('.bulk-order-charger-select');
    
    // Individual product vehicle dropdowns removed - now using hidden inputs
    // this.poInputs = document.querySelectorAll('.bulk-order-po-input');
    console.log("[cacheElements] Caching complete.");

    this.productRows.forEach(row => this.updateRowTotal(row.querySelector('.quantity__input')));
  }

  setupEventListeners() {
    // Global filter listeners
    if (this.filterYearSelect) {
      this.filterYearSelect.addEventListener('change', () => this.handleGlobalYearChange());
    }
    
    if (this.filterModelSelect) {
      this.filterModelSelect.addEventListener('change', () => this.handleGlobalModelChange());
    }

    if (this.clearFilterBtn) {
      this.clearFilterBtn.addEventListener('click', () => this.clearVehicleFilter());
    }

    // Individual vehicle selection listeners removed - now handled by global filters

    // this.poInputs.forEach(poInput => {
    //   poInput.addEventListener('input', (e) => this.handlePOChange(e));
    // });

    // Control button listeners
    if (this.selectAllBtn) {
      this.selectAllBtn.addEventListener('click', () => this.selectAllProducts());
    }
    
    if (this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', () => this.clearAllSelections());
    }

    // Form submission
    if (this.bulkForm) {
      this.bulkForm.addEventListener('submit', (e) => this.handleBulkSubmit(e));
    }

    // Product-specific listeners
    this.setupProductListeners();
  }

  setupProductListeners() {
    console.log('[setupProductListeners] Setting up listeners...');
    // Quantity input listeners are now attached in initializeProductRows for robustness.

    // Charger select listeners
    console.log('[setupProductListeners] Setting up listeners for ' + this.chargerSelects.length + ' charger selects.');
    this.chargerSelects.forEach((select, index) => {
      console.log(`[setupProductListeners] Attaching 'change' listener to charger select ${index + 1}:`, select);
      select.addEventListener('change', (e) => this.handleChargerChange(e));
    });
    console.log('[setupProductListeners] Finished setting up charger select listeners.');
  }

  async loadVehicles() {
    try {
      console.log('🚗 Loading vehicle data...');
      
      const query = `
        query getVehicles($type: String!, $first: Int!) {
          metaobjects(type: $type, first: $first) {
            edges {
              node {
                id
                fields {
                  key
                  value
                }
                handle
              }
            }
          }
        }
      `;

      const variables = {
        "type": "vehicle",
        "first": 250
      };

      const response = await fetch(window.location.origin + '/api/2023-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.config.storefront_access_token
        },
        body: JSON.stringify({ 
          query: query,
          variables: variables 
        })
      });

      if (!response.ok) {
        throw new Error('HTTP error! status: ' + response.status);
      }

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        throw new Error('GraphQL query failed');
      }

      if (!data.data?.metaobjects?.edges) {
        throw new Error('No vehicle data in response');
      }

      // Process the vehicle data
      this.vehicles = data.data.metaobjects.edges.map(edge => {
        const vehicleNode = edge.node;
        const fields = vehicleNode.fields.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {});

        return {
          year: parseInt(fields['vehicle.year'] || fields.year),
          model: fields['vehicle.model'] || fields.model,
          default_charger: fields['vehicle.default_charger'] || fields.default_charger
        };
      });

      console.log('✅ Loaded ' + this.vehicles.length + ' vehicles:', this.vehicles);
      
    } catch (error) {
      console.error('❌ Error loading vehicles:', error);
      this.vehicles = []; // Fallback to empty array
    }
  }

  initializeProductRows() {
    console.log('[initializeProductRows] Called.');
    const productRowElements = document.querySelectorAll('.bulk-order-row');
    console.log(`[initializeProductRows] Found ${productRowElements.length} product rows.`);

    productRowElements.forEach((row, index) => {
      console.log(`[initializeProductRows] Processing row ${index + 1}, ID: ${row.dataset.productId}`);
      const productDataEl = row.querySelector('.bulk-order-product-data');
      
      if (productDataEl && productDataEl.textContent) {
        console.log(`[initializeProductRows] Row ${row.dataset.productId}: Found productDataEl with text content.`);
        try {
          row.productData = JSON.parse(productDataEl.textContent);
          console.log(`[initializeProductRows] Row ${row.dataset.productId}: Successfully parsed productData:`, JSON.parse(JSON.stringify(row.productData)));
          
          if (!row.productData.hasOwnProperty('price')) {
            console.warn(`[initializeProductRows] Row ${row.dataset.productId}: productData is MISSING 'price' property.`);
          } else if (typeof row.productData.price !== 'number') {
            console.warn(`[initializeProductRows] Row ${row.dataset.productId}: productData.price is NOT a number. Type: ${typeof row.productData.price}, Value:`, row.productData.price);
          } else {
            console.log(`[initializeProductRows] Row ${row.dataset.productId}: productData.price is a number:`, row.productData.price);
          }

        } catch (error) {
          console.error(`[initializeProductRows] Row ${row.dataset.productId}: ❌ Error parsing product data. Error:`, error, 'Content:', productDataEl.textContent);
          row.productData = this.createFallbackProductData(row);
        }
      } else {
        console.warn(`[initializeProductRows] Row ${row.dataset.productId}: Product data script tag not found or empty. Creating fallback.`);
        row.productData = this.createFallbackProductData(row);
      }

      // Find and attach listener to the quantity input for THIS specific row
      const quantityInputForRow = row.querySelector('.quantity__input');
      if (quantityInputForRow) {
        console.log(`[initializeProductRows] Row ${row.dataset.productId}: Found quantity input. Attaching listeners.`, quantityInputForRow);
        quantityInputForRow.addEventListener('input', (e) => this.handleQuantityChange(e, 'input'));
        quantityInputForRow.addEventListener('change', (e) => this.handleQuantityChange(e, 'change'));
      } else {
        console.warn(`[initializeProductRows] Row ${row.dataset.productId}: Could NOT find .quantity__input within this row.`);
      }
    });
    console.log('[initializeProductRows] Finished processing all rows.');
  }

  createFallbackProductData(row) {
    // Create basic productData from HTML attributes as fallback
    const fallbackData = {
      id: parseInt(row.dataset.productId),
      price: 0, // Will be updated from price display
      show_variant_picker: false,
      selected_or_first_available_variant: {
        id: parseInt(row.dataset.variant) || 0,
        price: 0
      }
    };

    // Try to get price from the price display
    const priceEl = row.querySelector('.price');
    if (priceEl) {
      const priceText = priceEl.textContent.replace(/[^0-9.]/g, '');
      const priceValue = parseFloat(priceText);
      if (!isNaN(priceValue)) {
        fallbackData.price = Math.round(priceValue * 100); // Convert to cents
        fallbackData.selected_or_first_available_variant.price = fallbackData.price;
      }
    }

    console.log(`[createFallbackProductData] Created fallback data for ${row.dataset.productId}:`, fallbackData);
    return fallbackData;
  }

  handleYearChange(event) {
    const yearSelect = event.target;
    const productId = yearSelect.dataset.productId;
    console.log('📅 Year changed to:', yearSelect.value, 'for product', productId);
    this.rebuildModels(productId, yearSelect.value);
    this.resetChargerSelection(productId);
  }

  handleModelChange(event) {
    const modelSelect = event.target;
    const productId = modelSelect.dataset.productId;
    console.log('🚗 Model changed to:', modelSelect.value, 'for product', productId);
    this.checkAndApplyDefaults(productId);
  }

  handlePOChange(event) {
    const poInput = event.target;
    const productId = poInput.dataset.productId;
    this.updatePOProperty(productId, poInput.value);
    this.updateSubmitButton();
  }

  rebuildModels(productId, selectedYear) {
    const modelSelect = document.getElementById(`model-${productId}`);
    if (!modelSelect) return;

    modelSelect.innerHTML = '<option value="">Select Model</option>';
    const filteredVehicles = this.vehicles.filter(v => v.year == selectedYear);
    
    if (filteredVehicles.length === 0) {
      console.log('⚠️ No models found for year ' + selectedYear);
      this.resetChargerSelection(productId);
      return;
    }

    const uniqueModels = [...new Set(filteredVehicles.map(v => v.model))].sort();
    uniqueModels.forEach(model => {
      const option = new Option(model, model);
      modelSelect.appendChild(option);
    });

    console.log('📋 Populated ' + uniqueModels.length + ' models for year ' + selectedYear + ' for product ' + productId);
    
    if (uniqueModels.length === 1) {
      modelSelect.value = uniqueModels[0];
      setTimeout(() => this.checkAndApplyDefaults(productId), 10);
    }
  }

  checkAndApplyDefaults(productId) {
    const yearSelect = document.getElementById(`year-${productId}`);
    const modelSelect = document.getElementById(`model-${productId}`);
    
    if (!yearSelect?.value || !modelSelect?.value) {
      this.resetChargerSelection(productId);
      this.updateSubmitButton();
      return;
    }

    const selectedVehicle = this.vehicles.find(
      v => v.model === modelSelect.value && v.year == yearSelect.value
    );

    if (!selectedVehicle) {
      console.log('❌ No vehicle found for:', { year: yearSelect.value, model: modelSelect.value, productId });
      this.resetChargerSelection(productId);
      this.updateSubmitButton();
      return;
    }

    console.log('✅ Vehicle selected for product', productId, ':', selectedVehicle);

    // Enable and populate charger dropdown for this product
    this.populateChargerOptionsForProduct(productId);
    
    // Auto-select default charger if available
    if (selectedVehicle.default_charger) {
      console.log(`[checkAndApplyDefaults] Vehicle has default_charger: '${selectedVehicle.default_charger}'. Attempting to auto-select.`);
      this.autoSelectChargerForProduct(productId, selectedVehicle.default_charger);
    }

    // Update vehicle properties for this product
    this.updateVehiclePropertyForProduct(productId, `${modelSelect.value} • ${yearSelect.value}`);
    
    // Update submit button state
    this.updateSubmitButton();
  }

  populateChargerOptions() {
    this.chargerSelects.forEach(select => {
      select.disabled = false;
      select.innerHTML = `
        <option value="">Select Charger</option>
        <option value="CCS">CCS</option>
        <option value="NACS">NACS</option>
      `;
    });
    console.log('🔌 Populated charger options for all products');
  }

  populateChargerOptionsForProduct(productId) {
    const chargerSelect = document.getElementById(`charger-${productId}`);
    if (!chargerSelect) return;
    
    const row = document.querySelector(`[data-product-id="${productId}"].bulk-order-row`);
    if (!row?.productData?.show_variant_picker) return;
    
    chargerSelect.disabled = false;
    chargerSelect.innerHTML = '<option value="">Select Charger</option>';
    
    // Populate with actual product variants instead of hardcoded options
    if (row.productData.variants) {
      row.productData.variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = variant.option1;
        option.textContent = variant.option1;
        chargerSelect.appendChild(option);
      });
    }
    
    console.log('🔌 Populated charger options for product', productId, 'with actual variant options');
  }

  resetChargerSelection(productId) {
    console.log(`[resetChargerSelection] Called for product ${productId}.`);
    const chargerSelect = document.getElementById(`charger-${productId}`);
    if (chargerSelect) {
      // Don't disable the charger select. We just clear the pre-selection.
      // If a user had manually selected a charger, we could even choose to preserve it.
      // For now, let's reset it to prompt a choice if the vehicle changes.
      // chargerSelect.value = ''; // Uncomment if you want to force re-selection of charger when vehicle is cleared.
    }
    
    // Clear variant ID for this product if it has variant picker
    const row = document.querySelector(`[data-product-id="${productId}"].bulk-order-row`);
    if (row?.productData?.show_variant_picker) {
      const variantIdInput = row.querySelector('.bulk-order-variant-id');
      if (variantIdInput) {
        variantIdInput.value = '';
        console.log(`[resetChargerSelection] Cleared variant ID for product ${productId}`);
      }
    }
    
    console.log(`[resetChargerSelection] Charger state reset for product ${productId}, dropdown remains enabled.`);
  }

  autoSelectChargerForProduct(productId, defaultCharger) {
    const chargerSelect = document.getElementById(`charger-${productId}`);
    if (!chargerSelect) return;
    
    // Find the matching option value that contains the default charger
    // e.g., defaultCharger = "NACS" should match option value "CHARGER / NACS"
    let matchingOptionValue = null;
    
    Array.from(chargerSelect.options).forEach(option => {
      if (option.value && option.value.toUpperCase().includes(defaultCharger.toUpperCase())) {
        matchingOptionValue = option.value;
      }
    });
    
    if (matchingOptionValue) {
      chargerSelect.value = matchingOptionValue;
      
      // Don't show warning for auto-selection - only for manual selection
      // The system is picking the recommended charger based on vehicle
      
      this.updateVariantForCharger(chargerSelect, matchingOptionValue);
      
      // Update totals when charger is selected
      const row = chargerSelect.closest('.bulk-order-row');
      if (row) {
        this.updateRowTotal(chargerSelect);
        this.updateSummary();
        this.updateSubmitButton();
      }
      
      console.log('🎯 Auto-selected charger:', defaultCharger, '→', matchingOptionValue, 'for product', productId);
    } else {
      console.warn(`[autoSelectChargerForProduct] Could not find matching option for default charger "${defaultCharger}" in product ${productId}`);
    }
  }

  updateVehiclePropertyForProduct(productId, vehicleString) {
    const vehiclePropertyInput = document.querySelector(`[data-product-id="${productId}"].bulk-order-vehicle-property`);
    if (vehiclePropertyInput) {
      vehiclePropertyInput.value = vehicleString;
    }
  }

  updatePOProperty(productId, poValue) {
    const poPropertyInput = document.querySelector(`[data-product-id="${productId}"].bulk-order-po-property`);
    if (poPropertyInput) {
      poPropertyInput.value = poValue;
    }
  }

  resetChargerSelections() {
    this.chargerSelects.forEach(select => {
      select.disabled = true;
      select.innerHTML = '<option value="">Select Vehicle First</option>';
      select.value = '';
    });
    
    // Clear variant IDs for products with variant picker
    this.productRows.forEach(row => {
      if (row.productData?.show_variant_picker) {
        const variantIdInput = row.querySelector('.bulk-order-variant-id');
        if (variantIdInput) {
          variantIdInput.value = '';
        }
      }
    });
    
    console.log('🔄 Reset charger selections');
  }

  autoSelectCharger(defaultCharger) {
    this.chargerSelects.forEach(select => {
      select.value = defaultCharger;
      this.updateVariantForCharger(select, defaultCharger);
    });
    console.log('🎯 Auto-selected charger:', defaultCharger);
  }

  handleChargerChange(event) {
    const select = event.target;
    const chargerValue = select.value;
    
    // Show warning message when charger is selected
    this.showChargerWarning(select);
    
    this.updateVariantForCharger(select, chargerValue);
    this.updateRowTotal(select);
    this.updateSubmitButton();
  }

  showChargerWarning(chargerSelect) {
    const productId = chargerSelect.dataset.productId;
    const warningEl = document.getElementById(`charger-warning-${productId}`);
    
    if (warningEl) {
      warningEl.style.display = 'block';
    }
  }

  updateVariantForCharger(chargerSelect, chargerValue) {
    const productId = chargerSelect.dataset.productId;
    const row = document.querySelector(`[data-product-id="${productId}"].bulk-order-row`);
    
    if (!row || !row.productData?.show_variant_picker) {
      console.log(`[updateVariantForCharger] Skipping - no row or variant picker for product ${productId}`);
      return;
    }

    // Get variant map for this product
    const variantMapEl = row.querySelector(`[data-product-id="${productId}"].bulk-order-variant-map`);
    if (!variantMapEl) {
      console.error(`[updateVariantForCharger] No variant map found for product ${productId}`);
      return;
    }

    try {
      const variantMap = JSON.parse(variantMapEl.textContent);
      console.log(`[updateVariantForCharger] Product ${productId} - Looking for charger "${chargerValue}" (uppercase: "${chargerValue.toUpperCase()}") in variant map:`, variantMap);
      
      const variant = variantMap[chargerValue.toUpperCase()];
      
      if (variant) {
        const variantIdInput = row.querySelector('.bulk-order-variant-id');
        if (variantIdInput) {
          variantIdInput.value = variant.id;
          console.log('🔧 Set variant ID', variant.id, 'for product', productId, 'with charger', chargerValue);
          
          // Update price display
          this.updatePriceDisplay(row, variant);
          
          // Update row total immediately
          this.updateRowTotal(chargerSelect);
        } else {
          console.error(`[updateVariantForCharger] No variant ID input found for product ${productId}`);
        }
      } else {
        console.error(`[updateVariantForCharger] Variant not found for charger "${chargerValue.toUpperCase()}" in product ${productId}. Available keys:`, Object.keys(variantMap));
      }
    } catch (error) {
      console.error('[updateVariantForCharger] Error parsing variant map for product', productId, ':', error);
    }
  }

  updatePriceDisplay(row, variant) {
    const priceEl = row.querySelector('.bulk-order-price');
    if (priceEl && variant.price !== undefined) {
      // Format price (assuming price is in cents)
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(variant.price / 100);
      
      priceEl.innerHTML = `<span class="price">${formattedPrice}</span>`;
    }
  }

  updateVehicleProperties() {
    const vehicleValue = this.vehicleHidden.value;
    
    document.querySelectorAll('.bulk-order-vehicle-property').forEach(input => {
      input.value = vehicleValue;
    });
  }

  updatePONumbers() {
    const poValue = this.poNumberInput.value;
    
    document.querySelectorAll('.bulk-order-po-property').forEach(input => {
      input.value = poValue;
    });
  }

  handleQuantityChange(event, eventType = 'unknown') {
    const quantityInput = event.target;
    this.updateRowTotal(quantityInput);
    
    // Required attribute logic removed - now using global filters for vehicle selection

    this.updateSummary();
    this.updateSubmitButton();
  }

  updateRowTotal(element) {
    console.log('[updateRowTotal] Called. Element that triggered event:', element);
    const row = element.closest('.bulk-order-row');
    
    if (!row) {
      console.error('[updateRowTotal] Exiting: Could not find parent .bulk-order-row for element:', element);
      return;
    }
    console.log(`[updateRowTotal] Found row for product ID: ${row.dataset.productId}`, row);

    const quantityInput = row.querySelector('.quantity__input');
    const totalEl = row.querySelector('.bulk-order-total__amount');
    
    if (!quantityInput) {
      console.error(`[updateRowTotal] Exiting: Could not find .quantity__input in row ${row.dataset.productId}`);
      return;
    }
    if (!totalEl) {
      console.error(`[updateRowTotal] Exiting: Could not find .bulk-order-total__amount in row ${row.dataset.productId}`);
      return;
    }
    console.log(`[updateRowTotal] Row ${row.dataset.productId}: Found quantity input (value: "${quantityInput.value}") and total element.`);

    const quantity = parseInt(quantityInput.value) || 0;
    let price = 0; // Price in cents
    console.log(`[updateRowTotal] Row ${row.dataset.productId}: Parsed quantity: ${quantity}`);

    if (!row.productData) {
      console.warn(`[updateRowTotal] Row ${row.dataset.productId}: row.productData is missing. Price will remain 0.`);
    } else {
      console.log(`[updateRowTotal] Row ${row.dataset.productId}: Processing productData:`, JSON.parse(JSON.stringify(row.productData)));
      if (row.productData.show_variant_picker) {
        console.log(`[updateRowTotal] Row ${row.dataset.productId}: Product has variant picker.`);
        const chargerSelect = row.querySelector('.bulk-order-charger-select');
        const variantMapEl = row.querySelector('.bulk-order-variant-map');
        let variantFound = false;

        if (chargerSelect && variantMapEl && chargerSelect.value && variantMapEl.textContent.trim() !== '') {
          console.log(`[updateRowTotal] Row ${row.dataset.productId}: Attempting to get price from selected charger: "${chargerSelect.value}"`);
          try {
            const variantMap = JSON.parse(variantMapEl.textContent);
            const selectedChargerKey = chargerSelect.value.toUpperCase();
            if (variantMap[selectedChargerKey] && typeof variantMap[selectedChargerKey].price === 'number') {
              price = variantMap[selectedChargerKey].price;
              variantFound = true;
              console.log(`[updateRowTotal] Row ${row.dataset.productId}: Found price from selected charger variant: ${price}`);
            } else {
              console.warn(`[updateRowTotal] Row ${row.dataset.productId}: Selected charger variant "${selectedChargerKey}" not found in variantMap or price is not a number. Variant map:`, variantMap);
            }
          } catch (error) {
            console.error(`[updateRowTotal] Row ${row.dataset.productId}: Error parsing variantMap. Error:`, error);
          }
        }

        if (!variantFound) {
          console.log(`[updateRowTotal] Row ${row.dataset.productId}: No charger variant price found. Falling back to default product price.`);
          if (row.productData.price && typeof row.productData.price === 'number') {
            price = row.productData.price;
            console.log(`[updateRowTotal] Row ${row.dataset.productId}: Used row.productData.price: ${price}`);
          } else if (row.productData.selected_or_first_available_variant && typeof row.productData.selected_or_first_available_variant.price === 'number') {
            price = row.productData.selected_or_first_available_variant.price;
            console.log(`[updateRowTotal] Row ${row.dataset.productId}: Used selected_or_first_available_variant.price: ${price}`);
          } else {
            console.warn(`[updateRowTotal] Row ${row.dataset.productId}: Could not determine a default price for product with variant picker. All fallbacks failed.`);
          }
        }
      } else {
        console.log(`[updateRowTotal] Row ${row.dataset.productId}: Product does not have variant picker.`);
        if (row.productData.selected_or_first_available_variant && typeof row.productData.selected_or_first_available_variant.price === 'number') {
          price = row.productData.selected_or_first_available_variant.price;
          console.log(`[updateRowTotal] Row ${row.dataset.productId}: Used selected_or_first_available_variant.price: ${price}`);
        } else if (row.productData.price && typeof row.productData.price === 'number') {
          price = row.productData.price;
          console.log(`[updateRowTotal] Row ${row.dataset.productId}: Used row.productData.price: ${price}`);
        } else {
          console.warn(`[updateRowTotal] Row ${row.dataset.productId}: Could not determine price for product without variant picker. All fallbacks failed.`);
        }
      }
    }
    console.log(`[updateRowTotal] Row ${row.dataset.productId}: Final price before calculation: ${price} (cents)`);

    const total = (quantity * price) / 100; // Convert from cents
    const formattedTotal = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'CAD'
    }).format(total);

    console.log(`[updateRowTotal] Row ${row.dataset.productId}: Calculated total: ${total}, Formatted total: "${formattedTotal}"`);
    totalEl.textContent = formattedTotal;
    console.log(`[updateRowTotal] Row ${row.dataset.productId}: 🧮 Row total updated. Qty=${quantity}, Price/unit=${price/100}, Total=${formattedTotal}`);
  }

  updateSummary() {
    let totalItems = 0;
    let totalPrice = 0; // In cents

    // Check ALL products, not just selected ones
    document.querySelectorAll('.bulk-order-row').forEach(row => {
      const quantityInput = row.querySelector('.quantity__input');
      const quantity = parseInt(quantityInput?.value) || 0;
      
      if (quantity > 0) {
        totalItems += quantity;
        let unitPrice = 0; // Price in cents

        if (row.productData) {
          if (row.productData.show_variant_picker) {
            const chargerSelect = row.querySelector('.bulk-order-charger-select');
            const variantMapEl = row.querySelector('.bulk-order-variant-map');
            let variantFound = false;

            // Try to get price from selected charger variant
            if (chargerSelect && variantMapEl && chargerSelect.value && variantMapEl.textContent.trim() !== '') {
              try {
                const variantMap = JSON.parse(variantMapEl.textContent);
                const selectedChargerKey = chargerSelect.value.toUpperCase();
                if (variantMap[selectedChargerKey] && typeof variantMap[selectedChargerKey].price === 'number') {
                  unitPrice = variantMap[selectedChargerKey].price;
                  variantFound = true;
                }
              } catch (error) {
                console.error('Error in summary (variantMap) for ' + row.dataset.productId + ':', error);
              }
            }

            // If no specific charger variant, use product's base/default price
            if (!variantFound) {
              if (row.productData.price && typeof row.productData.price === 'number') {
                unitPrice = row.productData.price;
              } else if (row.productData.selected_or_first_available_variant && typeof row.productData.selected_or_first_available_variant.price === 'number') {
                unitPrice = row.productData.selected_or_first_available_variant.price;
              } else {
                 console.warn('Could not determine default unit price for summary (variant picker): ' + row.dataset.productId);
              }
            }
          } else if (row.productData.selected_or_first_available_variant && typeof row.productData.selected_or_first_available_variant.price === 'number') {
            // No variant picker
            unitPrice = row.productData.selected_or_first_available_variant.price;
          } else if (row.productData.price && typeof row.productData.price === 'number') {
            // Fallback for no variant picker
             unitPrice = row.productData.price;
          } else {
            console.warn('Could not determine unit price for summary: ' + row.dataset.productId);
          }
        } else {
           console.warn('row.productData missing for summary: ' + row.dataset.productId);
        }
        
        totalPrice += (quantity * unitPrice);
      }
    });

    // Update summary display
    if (this.summaryCount) {
      this.summaryCount.textContent = totalItems;
    }
    
    if (this.summaryPrice) {
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'CAD'
      }).format(totalPrice / 100);
      this.summaryPrice.textContent = formattedPrice;
    }
    
    console.log('📊 Summary: ' + totalItems + ' items, total: ' + totalPrice/100 + ' CAD');
  }

  updateSubmitButton() {
    if (!this.submitBtn) return;

    console.log(`[updateSubmitButton] Checking button state...`);
    console.log(`[updateSubmitButton] Found ${this.quantityInputs.length} quantity inputs`);
    
    const quantities = Array.from(this.quantityInputs).map(input => {
      const value = parseInt(input.value, 10) || 0;
      console.log(`[updateSubmitButton] Input value: "${input.value}" -> parsed: ${value}`);
      return value;
    });

    const hasItemsWithQuantity = quantities.some(qty => qty > 0);

    this.submitBtn.disabled = !hasItemsWithQuantity;
    console.log(`[updateSubmitButton] State check: hasItemsWithQuantity=${hasItemsWithQuantity}. Button disabled: ${this.submitBtn.disabled}`);
    console.log(`[updateSubmitButton] Quantities found: [${quantities.join(', ')}]`);
  }

  selectAllProducts() {
    // Set all quantities to 1 if they are 0
    this.quantityInputs.forEach(input => {
      if (parseInt(input.value) === 0) {
        input.value = 1;
        this.updateRowTotal(input);
      }
    });
    this.updateSummary();
    this.updateSubmitButton();
  }

  clearAllSelections() {
    // Clear all quantities
    this.quantityInputs.forEach(input => {
      input.value = 0;
      this.updateRowTotal(input);
    });

    // Clear all PO number inputs
    // const allPOInputs = this.bulkForm.querySelectorAll('.bulk-order-po-input');
    // allPOInputs.forEach(input => input.value = '');

    this.updateSummary();
    this.updateSubmitButton();
  }

  async handleBulkSubmit(event) {
    event.preventDefault();
    console.log('[handleBulkSubmit] Form submission intercepted.');

    if (!this.validateSubmission()) {
        return;
    }

    this.setSubmittingState(true);

    const items = this.prepareCartItems();
    console.log('[handleBulkSubmit] Prepared items for cart:', items);

    if (items.length === 0) {
      console.log('[handleBulkSubmit] No items to add. Aborting.');
      this.setSubmittingState(false);
      return;
    }

    const body = {
      items: items
    };

    const config = fetchConfig('javascript');
    config.body = JSON.stringify(body);

    try {
      // Step 1: Add items to cart
      console.log('[handleBulkSubmit] Making cart request to:', `${this.config.routes.cart_add}.js`);
      console.log('[handleBulkSubmit] Request config:', config);
      console.log('[handleBulkSubmit] Request body:', JSON.stringify(body));
      const initialResponse = await fetch(`${this.config.routes.cart_add}.js`, config);
      const cartData = await initialResponse.json();

      if (!initialResponse.ok) {
        let errorMessage = 'An unknown error occurred.';
        if (cartData.message) {
          errorMessage = cartData.message;
        }
        if (cartData.description) {
          errorMessage = cartData.description;
        }
        alert(errorMessage);
        throw new Error(errorMessage);
      }

      // Variable Grid: stamp the order-level CFR Program flag (one toggle for
      // the whole bulk order) as a cart attribute for create_order.js.
      const cfrToggle = document.getElementById('bulk-cfr-program-toggle');
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { 'CFR Program': cfrToggle && cfrToggle.checked ? 'Yes' : '' },
          }),
        });
      } catch (cfrErr) {
        console.error('⚠️ Failed to set CFR Program attribute:', cfrErr);
      }

      // Step 2: Fetch updated sections to render the notification
      console.log('[handleBulkSubmit] Cart response:', cartData);
      console.log('[handleBulkSubmit] Cart element found:', !!this.cart);
      
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        const sectionsToRender = this.cart.getSectionsToRender().map(section => section.id);
        console.log('[handleBulkSubmit] Sections to render:', sectionsToRender);
        
        const sectionsUrl = `${window.location.pathname}?sections=${sectionsToRender.join(',')}`;
        console.log('[handleBulkSubmit] Fetching sections from:', sectionsUrl);
        
        const sectionsResponse = await fetch(sectionsUrl);
        const sections = await sectionsResponse.json();
        console.log('[handleBulkSubmit] Sections response:', sections);

        // Get the last added item's key from the cart response
        // For bulk orders, we'll use the last item that was added
        const lastItemKey = cartData.items && cartData.items.length > 0 ? 
          cartData.items[cartData.items.length - 1].key : null;
        console.log('[handleBulkSubmit] Last item key:', lastItemKey);

        if (lastItemKey) {
          // Step 3: Render notification and update cart icon with proper data structure
          console.log('[handleBulkSubmit] Rendering cart notification...');
          this.cart.renderContents({
            key: lastItemKey,
            sections: sections
          });
        } else {
          console.log('[handleBulkSubmit] No item key found, updating cart icon only');
          // Fallback: Update cart icon only
          if (sections['cart-icon-bubble']) {
            const cartIcon = document.getElementById('cart-icon-bubble');
            if (cartIcon) {
              cartIcon.innerHTML = sections['cart-icon-bubble'];
            }
          }
        }
      } else {
        console.log('[handleBulkSubmit] Cart element not found or getSectionsToRender not available');
      }

      // Optional: Clear selections after successful submission
      this.clearAllSelections();

    } catch (error) {
      console.error('Error during bulk add to cart:', error);
      // You might want to show an error message to the user here
    } finally {
      this.setSubmittingState(false);
      console.log('[handleBulkSubmit] Submission process finished.');
    }
  }

  validateSubmission() {
    console.log('[validateSubmission] Validating submission...');
    const productsToAdd = Array.from(document.querySelectorAll('.bulk-order-row')).filter(row => {
      const quantityInput = row.querySelector('.quantity__input');
      return (parseInt(quantityInput?.value) || 0) > 0;
    });

    if (productsToAdd.length === 0) {
      alert('Please set a quantity for at least one product.');
      return false;
    }

    // Vehicle validation removed - now handled by global filters
    // All products with quantity > 0 are valid for submission
    return true;
  }

  prepareCartItems() {
    const items = [];
    console.log('[prepareCartItems] Starting to prepare items...');

    document.querySelectorAll('.bulk-order-row').forEach((row, index) => {
      const productId = row.dataset.productId;
      const productTitle = row.productData?.title || `Product ${index + 1}`;
      // console.log(`[prepareCartItems] Processing row ${index + 1}: ${productTitle} (ID: ${productId})`);

      const quantityInput = row.querySelector('.quantity__input');
      const rawQuantity = quantityInput?.value;
      const quantity = Number(rawQuantity) || 0;
      // console.log(`[prepareCartItems] Row ${productId}: Raw Qty: '${rawQuantity}', Parsed Qty: ${quantity}`);

      if (quantity <= 0) {
        // console.log(`[prepareCartItems] Row ${productId}: Skipping, quantity is <= 0.`);
        return; 
      }

      // Simplified variant ID logic - no more variant pickers, just use default variant
      let rawVariantId;
      
      // Try to get variant ID from multiple sources in order of preference
      if (row.productData?.selected_or_first_available_variant?.id) {
        rawVariantId = row.productData.selected_or_first_available_variant.id;
      } else if (row.dataset.variant) {
        rawVariantId = row.dataset.variant;
      } else {
        console.error(`[prepareCartItems] Row ${productId}: No variant ID found in productData or data attributes.`);
        return;
      }

      const variantId = Number(rawVariantId);

      if (!variantId || isNaN(variantId)) {
        console.error(`[prepareCartItems] Row ${productId}: Final variantId '${rawVariantId}' is missing or not a number. Skipping item.`);
        return; 
      }

      // console.log(`[prepareCartItems] Row ${productId}: Final numeric variantId for cart: ${variantId}`);

      const properties = {};
      
      // Get vehicle year and model from hidden inputs
      const yearInput = row.querySelector('.bulk-order-year-input');
      const modelInput = row.querySelector('.bulk-order-model-input');
      
      // Get PO number from visible input
      // const poInput = row.querySelector('.bulk-order-po-input');
      // if (poInput?.value) properties['PO Number'] = poInput.value;
      
      // Add separated vehicle properties (formatted for nice display in cart)
      if (yearInput?.value) properties['Vehicle Year'] = yearInput.value;
      if (modelInput?.value) properties['Vehicle Model'] = modelInput.value;
      
      // console.log(`[prepareCartItems] Row ${productId}: Properties:`, properties);

      items.push({
        id: variantId, // Ensure it's a number
        quantity: quantity, // Ensure it's a number
        properties: properties // Flat string pairs
      });
    });

    console.log('[prepareCartItems] Finished. Sanitized items for cart:', JSON.parse(JSON.stringify(items)));
    if (items.length === 0 && document.querySelectorAll('.quantity__input').some(q => (Number(q.value) || 0) > 0)) {
      console.warn('[prepareCartItems] No items were prepared for the cart, but some products had quantity > 0. Check variant ID logic.');
    }
    return items;
  }

  setSubmittingState(isSubmitting) {
    if (!this.submitBtn) return;

    if (isSubmitting) {
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('loading');
      const textEl = this.submitBtn.querySelector('.bulk-order-submit__text');
      if (textEl) {
        textEl.textContent = 'Adding to Cart...';
      }
    } else {
      // Re-check if there are products with quantity > 0
      console.log(`[setSubmittingState] Re-checking button state after submission...`);
      const rows = document.querySelectorAll('.bulk-order-row');
      console.log(`[setSubmittingState] Found ${rows.length} product rows`);
      
      const hasValidProducts = Array.from(rows).some(row => {
        const quantityInput = row.querySelector('.quantity__input');
        const quantity = parseInt(quantityInput?.value) || 0;
        console.log(`[setSubmittingState] Row ${row.dataset.productId}: quantity = ${quantity}`);
        return quantity > 0;
      });
      
      console.log(`[setSubmittingState] hasValidProducts = ${hasValidProducts}`);
      this.submitBtn.disabled = !hasValidProducts;
      console.log(`[setSubmittingState] Button disabled = ${this.submitBtn.disabled}`);
      
      this.submitBtn.classList.remove('loading');
      const textEl = this.submitBtn.querySelector('.bulk-order-submit__text');
      if (textEl) {
        textEl.textContent = 'Add to Cart';
      }
    }
  }

  // Global filter methods
  initializeGlobalFilters() {
    this.buildGlobalYearList();
  }

  buildGlobalYearList() {
    if (!this.filterYearSelect || !this.vehicles.length) return;
    
    const uniqueYears = [...new Set(this.vehicles.map(v => v.year))]
      .sort((a, b) => b - a); // newest first
    
    uniqueYears.forEach(year => {
      this.filterYearSelect.insertAdjacentHTML(
        'beforeend', 
        `<option value="${year}">${year}</option>`
      );
    });
    
    console.log('✅ Global year list built with existing vehicles:', uniqueYears);
  }

  handleGlobalYearChange() {
    if (!this.filterModelSelect || !this.filterYearSelect) return;
    
    // Clear model dropdown
    this.filterModelSelect.innerHTML = '<option value="">Select model</option>';
    this.filterModelSelect.disabled = !this.filterYearSelect.value;
    
    // Show/hide clear button
    this.updateClearButtonVisibility();
    
    if (!this.filterYearSelect.value) {
      this.filterRows(null);
      this.clearIndividualRowSelections();
      return;
    }

    // Populate models for selected year using existing vehicles data
    this.vehicles
      .filter(v => v.year == this.filterYearSelect.value)
      .forEach(v => {
        this.filterModelSelect.insertAdjacentHTML(
          'beforeend',
          `<option data-charger="${v.default_charger}" value="${v.model}">${v.model}</option>`
        );
      });

    // Populate individual row year dropdowns
    this.populateIndividualRowYears();
  }

  handleGlobalModelChange() {
    if (!this.filterModelSelect) return;
    
    const selectedOption = this.filterModelSelect.selectedOptions[0];
    const charger = selectedOption?.dataset.charger || null;
    this.filterRows(charger);
    
    // Show/hide clear button
    this.updateClearButtonVisibility();

    // Populate individual row model dropdowns
    this.populateIndividualRowModels();

    // Hide overlay when both year and model are selected
    this.updateOverlayVisibility();
  }

  filterRows(charger) {
    const UNIVERSAL = 'UNIVERSAL';
    
    this.filterProductRows.forEach(row => {
      const rowCharger = row.dataset.charger;
      const shouldShow = !charger || rowCharger === UNIVERSAL || rowCharger === charger;
      row.style.display = shouldShow ? '' : 'none';
    });
  }

  clearVehicleFilter() {
    // Reset dropdowns
    if (this.filterYearSelect) {
      this.filterYearSelect.value = '';
    }
    
    if (this.filterModelSelect) {
      this.filterModelSelect.innerHTML = '<option value="">Select model</option>';
      this.filterModelSelect.disabled = true;
    }
    
    // Show all rows
    this.filterRows(null);
    
    // Clear individual row selections
    this.clearIndividualRowSelections();
    
    // Hide clear button
    this.updateClearButtonVisibility();

    // Show overlay when filters are cleared
    this.updateOverlayVisibility();
  }

  updateClearButtonVisibility() {
    if (!this.clearFilterBtn) return;
    
    const hasActiveFilter = this.filterYearSelect?.value || this.filterModelSelect?.value;
    this.clearFilterBtn.style.display = hasActiveFilter ? 'block' : 'none';
  }

  updateOverlayVisibility() {
    const overlay = document.getElementById('vehicle-selection-overlay');
    if (!overlay) return;

    // Show overlay if either year or model is not selected
    const hasYear = this.filterYearSelect?.value;
    const hasModel = this.filterModelSelect?.value;
    const shouldShowOverlay = !hasYear || !hasModel;

    overlay.style.display = shouldShowOverlay ? 'flex' : 'none';
    
    // Also disable submit button when overlay is shown
    if (this.submitBtn) {
      if (shouldShowOverlay) {
        this.submitBtn.disabled = true;
      } else {
        // Re-run normal submit button update logic
        this.updateSubmitButton();
      }
    }
    
    console.log(`[updateOverlayVisibility] Year: ${hasYear}, Model: ${hasModel}, Overlay shown: ${shouldShowOverlay}`);
  }

  populateIndividualRowYears() {
    const selectedYear = this.filterYearSelect?.value;

    // Get all visible product rows and set year values
    const visibleRows = this.filterProductRows.filter(row => row.style.display !== 'none');
    
    visibleRows.forEach(row => {
      const productId = row.dataset.productId;
      
      // Set hidden year input value
      const yearInput = row.querySelector('.bulk-order-year-input');
      if (yearInput) {
        yearInput.value = selectedYear || '';
      }
      
      // Update display span
      const yearDisplay = row.querySelector('.bulk-order-vehicle-year');
      if (yearDisplay) {
        yearDisplay.textContent = selectedYear || '—';
      }
    });
  }

  populateIndividualRowModels() {
    const selectedModel = this.filterModelSelect?.value;

    // Get all visible product rows and set model values
    const visibleRows = this.filterProductRows.filter(row => row.style.display !== 'none');
    
    visibleRows.forEach(row => {
      const productId = row.dataset.productId;
      
      // Set hidden model input value
      const modelInput = row.querySelector('.bulk-order-model-input');
      if (modelInput) {
        modelInput.value = selectedModel || '';
      }
      
      // Update display span
      const modelDisplay = row.querySelector('.bulk-order-vehicle-model');
      if (modelDisplay) {
        modelDisplay.textContent = selectedModel || '—';
      }
    });
  }

  clearIndividualRowSelections() {
    // Clear all individual row hidden values and displays
    this.filterProductRows.forEach(row => {
      const productId = row.dataset.productId;
      
      // Clear hidden year input and display
      const yearInput = row.querySelector('.bulk-order-year-input');
      if (yearInput) {
        yearInput.value = '';
      }
      const yearDisplay = row.querySelector('.bulk-order-vehicle-year');
      if (yearDisplay) {
        yearDisplay.textContent = '—';
      }
      
      // Clear hidden model input and display
      const modelInput = row.querySelector('.bulk-order-model-input');
      if (modelInput) {
        modelInput.value = '';
      }
      const modelDisplay = row.querySelector('.bulk-order-vehicle-model');
      if (modelDisplay) {
        modelDisplay.textContent = '—';
      }
      
      // Clear vehicle property
      this.updateVehiclePropertyForProduct(productId, '');
    });
  }
}

// Initialize the bulk order manager
new BulkOrderManager(); 